import { Fragment, type ReactNode, useEffect, useMemo } from 'react'
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form'

import {
  type ContactReasonQuestion,
  formDataAdjusted,
} from '@/modules/contato/formData'

type PrimitiveAnswer = string | number | boolean | null | undefined
type AnswerValue = PrimitiveAnswer | PrimitiveAnswer[]
export type Answers = Record<string, AnswerValue>

const hasAnyAnswer = (value: AnswerValue): boolean => {
  if (Array.isArray(value)) {
    return value.some(hasAnyAnswer)
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }

  return value != null
}

const containsAllowedOption = (
  value: PrimitiveAnswer,
  allowed: readonly string[],
) => allowed.includes(String(value))

const arrayContainsAllowedOption = (
  values: PrimitiveAnswer[],
  allowed: readonly string[],
) => values.some((value) => containsAllowedOption(value, allowed))

const isOptionAllowed = (
  option: ContactReasonQuestion['options'][number] | undefined,
  answers: Answers,
) => {
  if (!option) {
    return false
  }

  return evaluateDependency(option.dependency, answers)
}

const enforceOptionDependencies = (
  question: ContactReasonQuestion,
  answers: Answers,
  value: AnswerValue,
): AnswerValue => {
  const options = question.options ?? []

  if (!options.length) {
    return value
  }

  if (!hasAnyAnswer(value)) {
    return value
  }

  if (Array.isArray(value)) {
    const filteredValues = value.filter((item) => {
      const option = options.find((opt) => opt.optionName === String(item))

      return isOptionAllowed(option, answers)
    })

    return filteredValues.length ? filteredValues : undefined
  }

  const option = options.find((opt) => opt.optionName === String(value))

  return isOptionAllowed(option, answers) ? value : undefined
}

const areAnswerValuesEqual = (a: AnswerValue, b: AnswerValue): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false
    }

    return a.every((value, index) => Object.is(value, b[index]))
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    return false
  }

  return Object.is(a, b)
}

export const evaluateDependency = (
  dependency: ContactReasonQuestion['dependency'],
  answers: Answers,
): boolean => {
  if (!dependency) {
    return true
  }

  const answeredValue = answers[dependency.dependsOnQuestionName]

  if (dependency.anyAnswer) {
    return hasAnyAnswer(answeredValue)
  }

  const allowedOptionNames = dependency.allowedOptionNames ?? []

  if (!allowedOptionNames.length) {
    return true
  }

  if (answeredValue == null) {
    return false
  }

  if (Array.isArray(answeredValue)) {
    return arrayContainsAllowedOption(answeredValue, allowedOptionNames)
  }

  return containsAllowedOption(answeredValue, allowedOptionNames)
}

export type RendererContext<FormValues extends FieldValues> = {
  control?: Control<FormValues>
  register?: UseFormRegister<FormValues>
  errors?: FieldErrors<FormValues>
  watch?: UseFormWatch<FormValues>
  clearErrors?: UseFormClearErrors<FormValues>
  setValue?: UseFormSetValue<FormValues>
  unregister?: UseFormUnregister<FormValues>
}

export type RendererParams<FormValues extends FieldValues> = {
  question: ContactReasonQuestion
  context: RendererContext<FormValues>
  answers: Answers
  options: ContactReasonQuestion['options']
  selectOptions: SelectOption[]
  isDependencySatisfied: boolean
}

type RendererFn<FormValues extends FieldValues> = (
  params: RendererParams<FormValues>,
) => ReactNode

export type SelectOption = { value: string; label: string }

const buildSelectOptions = (
  options?: ContactReasonQuestion['options'],
): SelectOption[] =>
  (options ?? [])
    .map((option) => {
      if (!option.optionName || !option.optionLabel) {
        return null
      }

      return {
        value: option.optionName,
        label: option.optionLabel,
      }
    })
    .filter((option): option is SelectOption => option !== null)

const getRenderableOptions = (
  options: ContactReasonQuestion['options'],
  answers: Answers,
) => options.filter((option) => evaluateDependency(option.dependency, answers))

type ContactFormStepProps<FormValues extends FieldValues = FieldValues> = {
  questionNames: string[]
  answers: Answers
  renderers?: Partial<Record<string, RendererFn<FormValues>>>
  defaultRenderers?: Partial<
    Record<ContactReasonQuestion['questionType'], RendererFn<FormValues>>
  >
  context?: RendererContext<FormValues>
}

const shouldClearQuestionValue = (
  shouldRender: boolean,
  currentValue: AnswerValue,
): boolean => {
  return !shouldRender && hasAnyAnswer(currentValue)
}

const ensureAnswerFitsDependencies = (
  question: ContactReasonQuestion,
  answers: Answers,
  currentValue: AnswerValue,
) => {
  if (!hasAnyAnswer(currentValue)) {
    return currentValue
  }

  return enforceOptionDependencies(question, answers, currentValue)
}

const clearQuestionValue = <FormValues extends FieldValues>(
  fieldName: Path<FormValues>,
  context: RendererContext<FormValues>,
) => {
  context.setValue?.(fieldName, undefined, {
    shouldValidate: false,
    shouldDirty: false,
  })
  context.unregister?.(fieldName)
  context.clearErrors?.(fieldName)
}

const syncQuestionValue = <FormValues extends FieldValues>(
  fieldName: Path<FormValues>,
  context: RendererContext<FormValues>,
  value: AnswerValue,
) => {
  context.setValue?.(fieldName, value as FormValues[typeof fieldName], {
    shouldValidate: false,
    shouldDirty: false,
  })
  context.clearErrors?.(fieldName)
}

const findQuestionByName = (
  questionName: string,
): ContactReasonQuestion | null =>
  formDataAdjusted.questions[questionName] ?? null

const shouldRenderQuestion = (
  question: ContactReasonQuestion,
  answers: Answers,
) => evaluateDependency(question.dependency, answers)

type ClearHiddenQuestionParams<FormValues extends FieldValues> = {
  shouldRender: boolean
  currentValue: AnswerValue
  fieldName: Path<FormValues>
  context: RendererContext<FormValues>
}

const clearValueIfQuestionHidden = <FormValues extends FieldValues>({
  shouldRender,
  currentValue,
  fieldName,
  context,
}: ClearHiddenQuestionParams<FormValues>): boolean => {
  if (!shouldClearQuestionValue(shouldRender, currentValue)) {
    return false
  }

  clearQuestionValue(fieldName, context)

  return true
}

type SyncAnswerIfChangedParams<FormValues extends FieldValues> = {
  fieldName: Path<FormValues>
  context: RendererContext<FormValues>
  currentValue: AnswerValue
  validAnswerValue: AnswerValue
}

const syncAnswerIfChanged = <FormValues extends FieldValues>({
  fieldName,
  context,
  currentValue,
  validAnswerValue,
}: SyncAnswerIfChangedParams<FormValues>) => {
  if (areAnswerValuesEqual(currentValue, validAnswerValue)) {
    return
  }

  syncQuestionValue(fieldName, context, validAnswerValue)
}

type SyncQuestionParams<FormValues extends FieldValues> = {
  questionName: string
  answers: Answers
  context: RendererContext<FormValues>
}

const syncQuestionByName = <FormValues extends FieldValues>({
  questionName,
  answers,
  context,
}: SyncQuestionParams<FormValues>) => {
  const question = findQuestionByName(questionName)

  if (!question) {
    return
  }

  const shouldRender = shouldRenderQuestion(question, answers)
  const currentValue = answers[questionName]
  const fieldName = questionName as Path<FormValues>

  if (
    clearValueIfQuestionHidden({
      shouldRender,
      currentValue,
      fieldName,
      context,
    })
  ) {
    return
  }

  const validAnswerValue = ensureAnswerFitsDependencies(
    question,
    answers,
    currentValue,
  )

  syncAnswerIfChanged({
    fieldName,
    context,
    currentValue,
    validAnswerValue,
  })
}

export const ContactFormStep = <FormValues extends FieldValues>({
  questionNames,
  answers,
  renderers = {},
  defaultRenderers,
  context,
}: ContactFormStepProps<FormValues>) => {
  const resolvedContext = useMemo(
    () => (context ?? {}) as RendererContext<FormValues>,
    [context],
  )

  useEffect(() => {
    if (!resolvedContext.setValue) {
      return
    }

    for (const questionName of questionNames) {
      syncQuestionByName<FormValues>({
        questionName,
        answers,
        context: resolvedContext,
      })
    }
  }, [answers, resolvedContext, questionNames])

  return (
    <>
      {questionNames.map((questionName) => {
        const question = formDataAdjusted.questions[questionName]

        if (!question) {
          return null
        }

        const shouldRender = evaluateDependency(question.dependency, answers)

        if (!shouldRender) {
          return null
        }

        const options = question.options
        const renderableOptions = getRenderableOptions(options, answers)
        const selectOptions = buildSelectOptions(renderableOptions)
        const hasOptions = options.length > 0
        const isDependencySatisfied =
          !hasOptions || renderableOptions.length > 0

        const renderer =
          renderers[questionName] ?? defaultRenderers?.[question.questionType]

        if (!renderer) {
          return null
        }

        return (
          <Fragment key={question.questionName}>
            {renderer({
              question,
              context: resolvedContext,
              answers,
              options: renderableOptions,
              selectOptions,
              isDependencySatisfied,
            })}
          </Fragment>
        )
      })}
    </>
  )
}
